import { AttributeType, StringSubType } from "@evergis/api";
import { Icon, IconButton, IJSXOption, OptionIcon, Tooltip } from "@evergis/ui";
import React, { FC, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { fieldTypes } from "../../../../../evergis/core/attributes/fieldTypes";
import { useToggle } from "../../../../../hooks/useToggle";
import {
  AttributeAliasContainer,
  AttributeAliasEdit,
  AttributeAliasText,
  NameInput,
  StyledIconButton,
  StyledPopup,
  TypeDropdown,
} from "./styled";

export interface AttributeAliasProps {
  isDisplayField?: boolean;
  alias: string;
  type?: AttributeType;
  subType?: StringSubType;
  updateAlias?: (alias: string) => void;
  updateType?: (type: AttributeType | StringSubType) => void;
}

const typesOptions = fieldTypes.filter(
  value => ![AttributeType.Point, AttributeType.MultiPolygon].includes(value.value as AttributeType),
);

export const AttributeAlias: FC<AttributeAliasProps> = ({
  isDisplayField,
  alias,
  updateAlias,
  type,
  subType,
  updateType,
}) => {
  const { t, i18n } = useTranslation("dialogs");
  const [isVisiblePopup, togglePopup] = useToggle(false);

  const [aliasText, setAliasText] = useState(alias);
  const [typeValue, setTypeValue] = useState(subType && subType !== StringSubType.None ? subType : type);

  const renderResultOptions = useCallback(selectedOptions => {
    if (selectedOptions && selectedOptions.length > 0) {
      return selectedOptions.map((item: IJSXOption) => {
        return (
          <OptionIcon key={item.value}>
            <Icon kind={item.icon} />
          </OptionIcon>
        );
      });
    }

    return null;
  }, []);

  const onMenuClick = useCallback(
    event => {
      event.stopPropagation();

      togglePopup();
    },
    [togglePopup],
  );

  const onClickSuccess = useCallback(
    event => {
      event.stopPropagation();

      updateAlias(aliasText);
      updateType(typeValue);
      togglePopup();
    },
    [togglePopup, aliasText, typeValue, updateType, updateAlias],
  );

  return (
    <AttributeAliasContainer>
      {!isDisplayField && <AttributeAliasText>{alias}</AttributeAliasText>}
      {isDisplayField && (
        <Tooltip arrow content={t("editResource.setAsDisplayField")}>
          {ref => (
            <AttributeAliasText ref={ref} isDisplayField>
              {alias}
            </AttributeAliasText>
          )}
        </Tooltip>
      )}
      <StyledPopup
        isVisible={isVisiblePopup}
        onRequestClose={togglePopup}
        anchor={ref => <StyledIconButton innerRef={ref} onClick={onMenuClick} kind={"edit"} />}
        placement={"bottom-start"}
      >
        <AttributeAliasEdit>
          <TypeDropdown
            value={typeValue}
            width={"3.5rem"}
            options={typesOptions.map(value => ({
              ...value,
              text: t(value.langKey, { ns: "common" }),
            }))}
            onChange={val => setTypeValue(val[0].value as AttributeType)}
            zIndex={10000}
            menuMinWidth={i18n.language === "en" ? "10rem" : "12rem"}
            renderResultOptions={renderResultOptions}
          />
          <NameInput
            style={{ marginRight: "0.5rem", borderRight: "0.0625rem solid rgba(0, 0, 0, 0.12)" }}
            width={"12rem"}
            value={aliasText}
            onChange={event => setAliasText(event.target.value)}
            placeholder={t("alias", { ns: "style" })}
          />
          <IconButton kind={"close"} error onClick={togglePopup} />
          <IconButton kind={"success"} primary onClick={onClickSuccess} />
        </AttributeAliasEdit>
      </StyledPopup>
    </AttributeAliasContainer>
  );
};
